import PropTypes from 'prop-types';
import React, { Component } from 'react';

const DRAG_THRESHOLD = 16;

const eventListenerOptionSupported = () => {
  let supported = false;
  try {
    const options = {
      get passive() { // This function will be called when the browser
                      //   attempts to access the passive property.
        supported = true;
        return false;
      }
    };

    window.addEventListener("test", null, options);
    window.removeEventListener("test", null, options);
  } catch(err) {
    supported = false;
  }
  return supported;
}

const passiveSupported = eventListenerOptionSupported();

export default class ImageDrawer extends Component {
  state = {
    map: this.props.map,
    down: false,
    dragging: false
  };
  
  constructor(props) {
    super(props);
    [
      'drawrect',
      'drawcircle',
      'drawpoly',
      'initCanvas',
      'renderPrefilledAreas',
    ].forEach((f) => (this[f] = this[f].bind(this)));
    let absPos = { position: 'absolute', top: 0, left: 0 };
    this.styles = {
      container: { position: 'relative', margin: '0 auto' },
      canvas: { ...absPos, pointerEvents: 'none', zIndex: 2 },
      img: { ...absPos, zIndex: 1, userSelect: 'none' },
    };
    // Props watched for changes to trigger update
    this.watchedProps = [
      'active',
      'fillColor',
      'height',
      'imgWidth',
      'lineWidth',
      'src',
      'strokeColor',
      'width'
    ];

    this.passiveOption = this.makePassiveEventOption(false);
    this.imageMouseDown = this.imageMouseDown.bind(this);
    this.imageMouseMove = this.imageMouseMove.bind(this);
    this.imageMouseUp = this.imageMouseUp.bind(this);
    this.imageOnWheel = this.imageOnWheel.bind(this);
    this.imageGestureStart = this.imageGestureStart.bind(this);
    this.imageGestureChange = this.imageGestureChange.bind(this);
    this.imageGestureEnd = this.imageGestureEnd.bind(this);
    this.imageTouchStart = this.imageTouchStart.bind(this);
    this.imageTouchMove = this.imageTouchMove.bind(this);
    this.imageTouchEnd = this.imageTouchEnd.bind(this);
  }

  makePassiveEventOption(passive) {
    return passiveSupported ? { passive } : passive;
  }

  componentDidMount() {
    // Panning on window to allow panning when mouse is out of wrapper
    window.addEventListener("mousedown", this.imageMouseDown, this.passiveOption);
    window.addEventListener("mousemove", this.imageMouseMove, this.passiveOption);
    window.addEventListener("mouseup", this.imageMouseUp, this.passiveOption);
    this.wrapper.addEventListener("wheel", this.imageOnWheel, this.passiveOption);
    this.wrapper.addEventListener("gesturestart", this.imageGestureStart, this.passiveOption);
    this.wrapper.addEventListener("gesturechange", this.imageGestureChange, this.passiveOption);
    this.wrapper.addEventListener("gestureend", this.imageGestureEnd, this.passiveOption);
    this.wrapper.addEventListener("touchstart", this.imageTouchStart, this.passiveOption);
    this.wrapper.addEventListener("touchmove", this.imageTouchMove, this.passiveOption);
    this.wrapper.addEventListener("touchend", this.imageTouchEnd, this.passiveOption);
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.imageMouseDown);
    window.removeEventListener("mousemove", this.imageMouseMove);
    window.removeEventListener("mouseup", this.imageMouseUp);
    this.wrapper.removeEventListener("wheel", this.imageOnWheel);
    this.wrapper.removeEventListener("gesturestart", this.imageGestureStart);
    this.wrapper.removeEventListener("gesturechange", this.imageGestureChange);
    this.wrapper.removeEventListener("gestureend", this.imageGestureEnd);
    this.wrapper.removeEventListener("touchstart", this.imageTouchStart);
    this.wrapper.removeEventListener("touchmove", this.imageTouchMove);
    this.wrapper.removeEventListener("touchend", this.imageTouchEnd);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.initCanvas();
    if (this.props.width !== prevProps.width) {
      var scale = this.props.width / prevProps.width;
      if (scale > 0) {
        this.wrapper.scrollLeft *= scale;
        this.wrapper.scrollTop *= scale;
        if (this.zoomEventPos) {
          this.wrapper.scrollLeft += this.wrapper.clientWidth * (scale - 1) * this.zoomEventPos.x;
          this.wrapper.scrollTop += this.wrapper.clientHeight * (scale - 1) * this.zoomEventPos.y;
        }
      }
    }
  }

  drawrect(coords, fillColor) {
    let [left, top, right, bot] = coords;
    this.ctx.fillStyle = fillColor;
    this.ctx.strokeRect(left, top, right - left, bot - top);
    this.ctx.fillRect(left, top, right - left, bot - top);
    this.ctx.fillStyle = this.props.fillColor;
  }

  drawhighlight(area) {
    const center = this.computeCenter(area);
    var strokeStyle = this.ctx.strokeStyle;
    var lineWidth = this.ctx.lineWidth;
    this.ctx.strokeStyle = '#ff0000ff';
    this.ctx.lineWidth = 5;
    this.ctx.beginPath();
    this.ctx.arc(center[0], center[1], this.scaleCoords([area.width + area.height])[0] / 2, 0, 2 * Math.PI);
    this.ctx.closePath();
    this.ctx.stroke();
    this.ctx.strokeStyle = strokeStyle;
    this.ctx.lineWidth = lineWidth;
  }
  
  drawcircle(coords, fillColor) {
    this.ctx.fillStyle = fillColor;
    this.ctx.strokeStyle = fillColor;
    this.ctx.beginPath();
    this.ctx.arc(coords[0], coords[1], coords[2], 0, 2 * Math.PI);
    this.ctx.closePath();
    this.ctx.stroke();
    this.ctx.fill();
    this.ctx.fillStyle = this.props.fillColor;
    this.ctx.strokeStyle = this.props.strokeColor;
  }

  drawpoly(coords, fillColor) {
    coords = coords.reduce(
      (a, v, i, s) => (i % 2 ? a : [...a, s.slice(i, i + 2)]),
      []
    );
    this.ctx.fillStyle = fillColor;
    this.ctx.beginPath();
    let first = coords.unshift();
    this.ctx.moveTo(first[0], first[1]);
    coords.forEach((c) => this.ctx.lineTo(c[0], c[1]));
    this.ctx.closePath();
    this.ctx.stroke();
    this.ctx.fill();
    this.ctx.fillStyle = this.props.fillColor;
  }

  initCanvas() {
    if (this.props.width) this.img.width = this.props.width;

    if (this.props.height) this.img.height = this.props.height;

    this.canvas.width = this.props.width || this.img.clientWidth;
    this.canvas.height = this.props.height || this.img.clientHeight;
    this.container.style.width =
      (this.props.width || this.img.clientWidth) + 'px';
    this.container.style.height =
      (this.props.height || this.img.clientHeight) + 'px';
    this.ctx = this.canvas.getContext('2d');
    this.ctx.fillStyle = this.props.fillColor;
    this.ctx.strokeStyle = this.props.strokeColor;
    this.ctx.lineWidth = this.props.lineWidth;

    if (this.props.onLoad) this.props.onLoad();

    this.renderPrefilledAreas();
  }

  hoverOn(area, index, event) {
    const shape = event.target.getAttribute('shape');

    if (!this.state.dragging && this.props.active && this['draw' + shape])
      this['draw' + shape](
        event.target.getAttribute('coords').split(','),
        area.fillColor
      );

    if (this.props.onMouseEnter) this.props.onMouseEnter(area, index, event);
  }

  hoverOff(area, index, event) {
    if (this.props.active) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.renderPrefilledAreas();
    }

    if (this.props.onMouseLeave) this.props.onMouseLeave(area, index, event);
  }

  click(area, index, event) {
    if (!this.state.dragging && !this.wasDragging && this.props.onClick) {
      event.preventDefault();
      this.props.onClick(event.pageX - this.container.offsetLeft, event.pageY - this.container.offsetTop/*area.center[1] - this.wrapper.scrollTop*/, area, index, event);
    }
  }

  imageClick(event) {
    if (this.props.onImageClick) {
      event.preventDefault();
      this.props.onImageClick(event);
    }
  }

  mouseMove(area, index, event) {
    if (this.props.onMouseMove) {
      this.props.onMouseMove(area, index, event);
    }
  }

  imageMouseUp(event) {
    this.setState( { down: false } );
    if (this.state.dragging) {
      this.setState( { dragging: false } );
      this.wasDragging = true;
    }
    event.preventDefault();
  }

  imageMouseDown(event) {
    this.startX = event.screenX;
    this.startY = event.screenY;
    this.setState ( { dragging: false, down: true });
    this.wasDragging = false;
    event.preventDefault();
  }

  imageMouseMove(event) {
    // can't use event.movementX for IE support
    var movementX = event.screenX - this.startX;
    var movementY = event.screenY- this.startY;
    if (this.state.down && !this.state.dragging) {
      if (Math.abs(movementX) > DRAG_THRESHOLD || Math.abs(movementY)) {
        this.setState( { dragging: true } );
      }
    }
    if (this.wrapper && this.state.down && this.state.dragging) {
      this.wrapper.scrollLeft -= movementX;
      this.wrapper.scrollTop -= movementY;
      this.startX = event.screenX;
      this.startY = event.screenY;
    }
    event.preventDefault();
  }

  imageOnWheel(event) {
    this.zoomEventPos = {x: event.clientX / this.wrapper.clientWidth, y: event.clientY / this.wrapper.clientHeight};
    if (this.props.onImageWheel) {
      this.props.onImageWheel(event);
    } else {
      let { width } = this.props;
      if (event.deltaY < 0) {
        width *= 1.2;
      } else {
        width /= 1.2;
      }
      this.setWidth(width);
      event.preventDefault();
    }
  }

  imageGestureStart(event) {
    // console.log('imageGestureStart');
    event.preventDefault();
    this.zoomEventPos = {x: event.clientX / this.wrapper.clientWidth, y: event.clientY / this.wrapper.clientHeight};
    this.initialGestureWidth = this.props.width;
    this.haveGesture = true;
  }

  imageGestureChange(event) {
    // console.log('imageGestureChange ' + event.scale);
    this.setWidth(this.initialGestureWidth * event.scale);
    event.preventDefault();
  }

  imageGestureEnd(event) {
    // console.log('imageGestureEnd ' + event.scale);
    this.zoomEventPos = {x: event.clientX / this.wrapper.clientWidth, y: event.clientY / this.wrapper.clientHeight};
    this.setWidth(this.initialGestureWidth * event.scale);
    event.preventDefault();
  }

  imageTouchStart(event) {
    if (!this.haveGesture && event.touches.length === 2) {
      // console.log('imageTouchStart ' + event.touches.length);
      this.zoomEventPos = {x: ((event.touches[0].clientX + event.touches[0].clientX) / 2) / this.wrapper.clientWidth,
                                y: ((event.touches[0].clientY + event.touches[1].clientY) / 2) / this.wrapper.clientHeight};
      this.touchStartDist = Math.hypot(event.touches[0].clientX - event.touches[0].clientX, event.touches[0].clientY - event.touches[1].clientY);
      event.preventDefault();
      this.initialGestureWidth = this.props.width;
    }
  }

  imageTouchMove(event) {
    if (!this.haveGesture && event.touches.length === 2 && this.touchStartDist > 0) {
      // console.log('imageTouchMove ' + event.touches.length);
      var newTouchStartDist = Math.hypot(event.touches[0].clientX - event.touches[0].clientX, event.touches[0].clientY - event.touches[1].clientY);
      this.setWidth(this.initialGestureWidth * (newTouchStartDist / this.touchStartDist));
      event.preventDefault();
    }
  }

  imageTouchEnd(event) {
    if (!this.haveGesture && event.touches.length === 2 && this.touchStartDist > 0) {
      // console.log('imageTouchEnd ' + event.touches.length);
      var newTouchStartDist = Math.hypot(event.touches[0].clientX - event.touches[0].clientX, event.touches[0].clientY - event.touches[1].clientY);
      this.setWidth(this.initialGestureWidth * (newTouchStartDist / this.touchStartDist));
      event.preventDefault();
    }
  }

  buttonZoom(zoom) {
    let { width } = this.props;
    const factor = 1.5;
    if (zoom === 'in') {
      width *= factor;
      this.setWidth(width);
    }
    if (zoom === 'out') {
      width /= factor;
      this.setWidth(width);
    }
  };
    
  buttonZoomIn() {
    this.zoomEventPos = {x: 0.5, y: 0.5};
    if (this.props.onButtonZoom) {
      this.props.onButtonZoom('in');
    } else {
      this.buttonZoom('in');
    }
  }
  
  buttonZoomOut() {
    this.zoomEventPos = {x: 0.5, y: 0.5};
    if (this.props.onButtonZoom) {
      this.props.onButtonZoom('out');
    } else {
      this.buttonZoom('out');
    }
  }
  
  prevent(event) {
    event.preventDefault();
  }

  scaleCoords(coords) {
    const { imgWidth, width } = this.props;
    // calculate scale based on current 'width' and the original 'imgWidth'
    const scale = width && imgWidth && imgWidth > 0 ? width / imgWidth : 1;
    return coords.map((coord) => coord * scale);
  }

  renderPrefilledAreas() {
    this.props.map.areas.map((area) => {
      if (!area.preFillColor) return area;
      this['draw' + area.shape](
        this.scaleCoords(area.coords),
        area.preFillColor
      );
      this.drawhighlight(area);
      return area;
    });
  }

  computeCenter(area) {
    if (!area) return [0, 0];

    switch (area.shape) {
      case 'circle':
        const scaledCoords = this.scaleCoords(area.coords);
        return [scaledCoords[0], scaledCoords[1]];
      case 'rect':
      case 'poly':
      default: {
        return this.scaleCoords([area.x + area.width / 2, area.y + area.height / 2]);
      }
    }
  }

  renderAreas() {
    return this.props.map.areas.filter((area) => area.id).map((area, index) => {
      const scaledCoords = this.scaleCoords(area.coords);
      const center = this.computeCenter(area);
      const extendedArea = { ...area, scaledCoords, center };
      return (
        <area
          key={area.id || index}
          alt={area.id}
          shape={area.shape}
          coords={scaledCoords.join(',')}
          onMouseEnter={this.hoverOn.bind(this, extendedArea, index)}
          onMouseLeave={this.hoverOff.bind(this, extendedArea, index)}
          onMouseMove={this.mouseMove.bind(this, extendedArea, index)}
          onClick={this.click.bind(this, extendedArea, index)}
          href={area.href}
        />
      );
    });
  }

  setWidth(width) {
    if (width < 320) {
      width = 320;
    }
    if (width > this.props.imgWidth) {
      width = this.props.imgWidth;
    }
    if (width !== this.props.width) {
      this.props.setStore({ width });
    }
  }

  cursorShape() {
    if (this.wrapper && this.container && (this.wrapper.clientWidth >= this.container.clientWidth && this.wrapper.clientHeight >= this.container.clientHeight)) {
      return 'default';
    } else if (this.state.down) {
      if (this.state.dragging) {
        return 'grabbing';
      } else {
        return 'move';
      }
    } else {
      return 'grab';
    }
  }

  render() {
    return (
    <div className="imageWrapper" style={{ position: 'relative', cursor: this.cursorShape() }}
            ref={(node) => (this.wrapper = node)}>
      <div className="header">
        <button onClick={this.buttonZoomIn.bind(this)} key="in" style={{ cursor: 'zoom-in' }}>
          +
        </button>
        <button onClick={this.buttonZoomOut.bind(this)} key="out" style={{ cursor: 'zoom-out' }}>
          -
        </button>
      </div>
      <div id="imageMapper"
        style={this.styles.container}
        ref={(node) => (this.container = node)}
      >
        <img
          draggable="false"
          style={this.styles.img}
          src={this.props.src}
          useMap={`#${this.props.map.name}`}
          alt=""
          ref={(node) => (this.img = node)}
          onLoad={this.initCanvas}
          onClick={this.imageClick.bind(this)}
          onDragStart={this.prevent}
        />
        <canvas
          ref={(node) => (this.canvas = node)}
          style={this.styles.canvas}
        />
        <map name={this.props.map.name} style={{ cursor: this.cursorShape() === 'grabbing' ? 'grabbing' : 'pointer' }}>
          {this.renderAreas()}
        </map>
      </div>
    </div>
    );
  }
}

ImageDrawer.defaultProps = {
  active: true,
  fillColor: 'rgba(255, 255, 255, 0.5)',
  lineWidth: 1,
  map: {
    areas: [],
    name: 'image-map-' + Math.random(),
  },
  strokeColor: 'rgba(0, 0, 0, 0.5)',
};

ImageDrawer.propTypes = {
  active: PropTypes.bool,
  fillColor: PropTypes.string,
  height: PropTypes.number,
  imgWidth: PropTypes.number,
  lineWidth: PropTypes.number,
  src: PropTypes.string.isRequired,
  strokeColor: PropTypes.string,
  width: PropTypes.number,

  onClick: PropTypes.func,
  onMouseMove: PropTypes.func,
  onImageClick: PropTypes.func,
  onImageMouseMove: PropTypes.func,
  onLoad: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onButtonZoom: PropTypes.func,
  setStore: PropTypes.func.isRequired,

  map: PropTypes.shape({
    areas: PropTypes.arrayOf(
      PropTypes.shape({
        area: PropTypes.shape({
          coords: PropTypes.arrayOf(PropTypes.number),
          href: PropTypes.string,
          shape: PropTypes.string,
          preFillColor: PropTypes.string,
          fillColor: PropTypes.string,
        }),
      })
    ),
    name: PropTypes.string,
    displayName: PropTypes.string
  }),
};
